import { selectCurrentHotelNumberOfRooms, selectCurrentHotelPMS } from 'accessors'
import Button from 'components/ReusableComponents/Button'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import AccessTimeIcon from 'components/ReusableComponents/Icons/AccessTime'
import DateIcon from 'components/ReusableComponents/Icons/Date'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import RefreshIcon from 'components/ReusableComponents/RefreshIcon'
import Toggle from 'components/ReusableComponents/Toggle'
import UploadLogsDayRow from 'features/PMSLogs/DayRow'
import { PMSs } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import pmsApi from 'services/api/pms'

import DeleteLogsDialog from './DeleteDialog/DeleteDialog'
import MoveLogsDialog from './MoveDialog/MoveDialog'
import styles from './PMSLogs.module.scss'
import { verifyOnboardingData } from './verifyOnboardingData'

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

const AllowedPMSsForEditing = [
  PMSs.operadefault,
  PMSs.opera,
  PMSs.marriottgxp,
  PMSs.pep,
  PMSs.stayntouch,
  PMSs.choiceadvantage
]

const Tab = ({ onClick, children, isActive }) => (
  <button
    className={[styles.tab, isActive ? styles.active : ''].join(' ')}
    onClick={onClick}
  >
    {children}
  </button>
)

const Tabs = ({ children }) => <div className={styles.tabs}>{children}</div>

const PMSLogs = ({ onVerificationChange }) => {
  const [lastUpdated, setLastUpdated] = useState()

  const currentPMS = useSelector(selectCurrentHotelPMS)
  const numberOfRooms = useSelector(selectCurrentHotelNumberOfRooms)
  const isEditingAllowed = AllowedPMSsForEditing.includes(currentPMS)

  const [month, setMonth] = useState(moment().month() + 1)
  const [year, setYear] = useState(moment().year())

  const [tabs, setTabs] = useState()
  const [activeTab, setActiveTab] = useState()

  const [isErred, setIsErred] = useState(false)
  const [error, setError] = useState()

  const [isEditing, setIsEditing] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])

  const [isMoveModalVisible, setIsMoveModalVisible] = useState(false)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)

  const [isFetchingLogs, setIsFetchingLogs] = useState(true)

  const [data, setData] = useState()

  const getLogs = useCallback(() => {
    setActiveTab(null)
    setIsFetchingLogs(true)
    pmsApi
      .histories({ month, year })
      .then(res => {
        setLastUpdated(new Date().toJSON())
        setData(res)
        const newTabs = [...new Set(res.logs.map(log => log.name))]
        setTabs(newTabs)
        setActiveTab(currentActiveTab => currentActiveTab || newTabs[0])
        setIsFetchingLogs(false)
        setIsErred(false)
      })
      .catch(err => {
        setIsErred(true)
        setIsFetchingLogs(false)
        setError(err.message)
      })
  }, [month, year])

  useEffect(() => {
    getLogs()
  }, [getLogs])

  const onPressMove = () => {
    setIsMoveModalVisible(true)
  }
  const onCloseMove = () => {
    setIsMoveModalVisible(false)
  }

  const onPressDelete = () => {
    setIsDeleteModalVisible(true)
  }
  const onCloseDelete = () => {
    setIsDeleteModalVisible(false)
  }

  const activeTabInfo = data && data.logs.find(log => log.name === activeTab)?.info

  const activeTabLogs = useMemo(
    () =>
      (data?.logs.find(log => log.name === activeTab)?.logs ?? []).flatMap(
        day => day.logs ?? []
      ),
    [data, activeTab]
  )

  const onboardingVerification = useMemo(
    () => verifyOnboardingData({ logs: activeTabLogs, numberOfRooms }),
    [activeTabLogs, numberOfRooms]
  )

  useEffect(() => {
    onVerificationChange?.(activeTabLogs.length > 0 ? onboardingVerification : null)
  }, [activeTabLogs.length, onboardingVerification, onVerificationChange])

  return (
    <div className={styles.wrapper}>
      <CustomHeader
        title="Upload logs"
        subtitle={`Last updated on: ${moment(lastUpdated).format('HH:mm:ss')}`}
        width={1200}
      >
        <RefreshIcon onClick={getLogs} className={styles.refreshIcon} />

        {isEditingAllowed && (
          <Toggle
            isGray={true}
            checked={isEditing}
            label="Edit mode"
            handleChange={() => setIsEditing(!isEditing)}
          />
        )}
      </CustomHeader>
      {isFetchingLogs && (
        <div className={styles.logsHolder}>
          <LoadingIndicator />
        </div>
      )}
      {isErred && <p className={styles.errorText}>{error}</p>}
      {!isFetchingLogs && !isErred && (
        <>
          <Tabs>
            {tabs.map(tab => (
              <Tab
                key={tab}
                to={tab}
                onClick={() => setActiveTab(tab)}
                isActive={activeTab === tab}
              >
                {data.logs.find(log => log.name === tab)?.info?.name ||
                  data.logs.find(log => log.name === tab)?.name}
              </Tab>
            ))}
            <div className={styles.filters}>
              <Dropdown
                value={month}
                options={
                  year === moment().year()
                    ? Array(moment().month() + 1)
                        .fill(0)
                        .map((_, i) => moment().subtract(i, 'months').month() + 1)
                        .reduce(
                          (acc, month) => ({ ...acc, [month]: monthNames[month - 1] }),
                          {}
                        )
                    : monthNames.reduce(
                        (acc, month, i) => ({ ...acc, [i + 1]: month }),
                        {}
                      )
                }
                onChange={e => setMonth(e.target.value)}
                isSortedAlphabetically={false}
              />
              <Dropdown
                isSortedAlphabetically={false}
                value={year}
                onChange={e => setYear(e.target.value)}
                options={Array(8)
                  .fill(0)
                  .map((_, i) => moment().subtract(i, 'years').year())
                  .reduce((acc, year) => ({ ...acc, [year]: `${year}` }), {})}
              />
            </div>
          </Tabs>
          {activeTabInfo?.description && (
            <div className={styles.reportInfoBar}>
              <div className={styles.reportInfoBarTitle}>
                {activeTabInfo?.description}
              </div>
              <div className={styles.reportInfoBarDetails}>
                <ul>
                  <li>
                    <AccessTimeIcon /> <span>{activeTabInfo.frequency}</span>
                  </li>
                  <li>
                    <DateIcon /> <span>{activeTabInfo.timeRange}</span>
                  </li>
                </ul>
              </div>
            </div>
          )}
          <div className={styles.logsHolder}>
            {data &&
              data.logs.length > 0 &&
              data?.logs
                .find(log => log.name === activeTab)
                .logs.map(day => (
                  <UploadLogsDayRow
                    logGroup={day}
                    key={day.date}
                    isEditing={isEditing}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    reportEstimatesById={onboardingVerification.reportEstimatesById}
                  />
                ))}

            {data.logs.length === 0 && <div className={styles.noLogs}>No logs found</div>}
          </div>
        </>
      )}

      {isEditing && (
        <div className={styles.footer}>
          <span className={styles.label}>
            {selectedRows.length === 0
              ? 'No log is selected'
              : selectedRows.length === 1
              ? '1 log is selected'
              : `${selectedRows.length} logs are selected`}
          </span>

          <Button onClick={onPressMove} disabled={!selectedRows.length}>
            Move to another hotel
          </Button>
          <Button
            onClick={onPressDelete}
            className={styles.deleteButton}
            disabled={!selectedRows.length}
          >
            Delete
          </Button>
        </div>
      )}

      {isMoveModalVisible && (
        <MoveLogsDialog
          onClose={onCloseMove}
          getLogs={getLogs}
          logs={data.logs}
          selectedRows={selectedRows}
          month={month}
          year={year}
        />
      )}
      {isDeleteModalVisible && (
        <DeleteLogsDialog
          onClose={onCloseDelete}
          logs={data.logs}
          selectedRows={selectedRows}
          month={month}
          year={year}
        />
      )}
    </div>
  )
}

export default PMSLogs
